import { ChevronLeft, ChevronRight } from 'lucide-react'
import { formatNumber } from '@najah/core'
import { cn } from '../lib/cn.js'

export interface PaginationProps {
  page: number
  totalPages: number
  total: number
  pageSize: number
  onPageChange: (page: number) => void
  onPageSizeChange?: (size: number) => void
}

const PAGE_SIZES = [25, 50, 100]

export function Pagination({ page, totalPages, total, pageSize, onPageChange, onPageSizeChange }: PaginationProps) {
  if (total === 0) return null

  const first = (page - 1) * pageSize + 1
  const last = Math.min(page * pageSize, total)

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3 text-sm">
      <p className="text-fg-muted">
        عرض <span className="font-medium text-fg" dir="ltr">{formatNumber(first)}–{formatNumber(last)}</span> من{' '}
        <span className="font-medium text-fg" dir="ltr">{formatNumber(total)}</span>
      </p>

      <div className="flex items-center gap-2">
        {onPageSizeChange && (
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="h-8 rounded-lg border border-border bg-surface px-2 text-xs text-fg-muted focus:outline-none focus:border-primary"
            aria-label="عدد الصفوف في الصفحة"
          >
            {PAGE_SIZES.map((s) => (
              <option key={s} value={s}>{s} / صفحة</option>
            ))}
          </select>
        )}

        <div className="flex items-center gap-1">
          {/* في RTL «السابق» يشير يميناً — نستعمل الأيقونة المطابقة بصرياً */}
          <button
            type="button"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            className={cn(
              'grid size-8 place-items-center rounded-lg border border-border text-fg-muted transition-colors',
              'hover:bg-surface-hover hover:text-fg disabled:opacity-40 disabled:pointer-events-none',
            )}
            aria-label="الصفحة السابقة"
          >
            <ChevronRight className="size-4" />
          </button>

          <span className="px-2 text-xs text-fg-muted" dir="ltr">
            {formatNumber(page)} / {formatNumber(totalPages)}
          </span>

          <button
            type="button"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            className={cn(
              'grid size-8 place-items-center rounded-lg border border-border text-fg-muted transition-colors',
              'hover:bg-surface-hover hover:text-fg disabled:opacity-40 disabled:pointer-events-none',
            )}
            aria-label="الصفحة التالية"
          >
            <ChevronLeft className="size-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
